/**
 * DealViabilityInsight
 *
 * Helper content under the offer-amount field telling an investor how likely their offer is to
 * reach a conversation with the seller, and — when they are close — what it would take to
 * improve those odds.
 *
 * The figures come from the server. `floor_price` is gated on `share_floor_price`, so for most
 * properties the browser never receives the floor and cannot compute the ratio this keys on.
 *
 * Renders nothing at all when there is nothing to say: no placeholder, no hole in the layout.
 */

import { Redact } from '@/common/analytics'
import type { ViabilityTier } from '@/features/offers/api/offers'
import type { OfferViability } from '@/features/offers/api/offers'
import { viabilityText } from './copy'
import * as styles from './DealViabilityInsight.css'

// Inline stroke SVGs rather than icon-system assets, which are filled and would read as a
// status chip rather than a quiet marker. Paths are from the design handoff.
const TIER_ICONS: Record<ViabilityTier, React.ReactNode> = {
  t1: (
    <>
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L14.71 3.86a2 2 0 00-3.42 0z" />
    </>
  ),
  t2: (
    <>
      <path d="M6 3h12l4 6-10 12L2 9Z" />
      <path d="M11 3 8 9l4 12 4-12-3-6" />
      <path d="M2 9h20" />
    </>
  ),
  t3: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
}

export interface DealViabilityInsightProps {
  /** Server response, or undefined while it has not been fetched. */
  insight?: OfferViability | null
  /** The offer amount the insight was computed for, used in the copy. */
  offerPrice: number
}

export function DealViabilityInsight({ insight, offerPrice }: DealViabilityInsightProps) {
  if (!insight?.show || !insight.tier) return null

  // At or above the floor price there is no card at all — that state is a quiet tick on the
  // field itself, rendered by DealViabilityMark.
  const text = viabilityText(insight, offerPrice)
  if (!text) return null

  const tier = insight.tier

  // The text quotes the offer amount, which is PII, so redaction lives here rather than around
  // the component. Wrapping it at the call site leaves an empty element behind on every
  // suppressed render, and the form is a flex column — an empty child still takes a gap.
  return (
    <Redact as="div">
      <div
        className={`${styles.card} ${styles.cardTier[tier]}`}
        role="status"
      >
        <svg
          className={`${styles.icon} ${styles.iconTier[tier]}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {TIER_ICONS[tier]}
        </svg>
        <div className={`${styles.text} ${styles.textTier[tier]}`}>{text}</div>
      </div>
    </Redact>
  )
}
